import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { apiGet } from '../../../services/api';
import { EMPLOYMENT_TYPES, SENIORITY_OPTIONS } from './wizardConstants';
import { useJobWizard } from './JobWizardContext';

const MAX_SKILLS = 10;

function flattenGroupedSkills(grouped) {
  if (!grouped || typeof grouped !== 'object') return [];
  const items = [];
  Object.entries(grouped).forEach(([category, skills]) => {
    if (!Array.isArray(skills)) return;
    skills.forEach((skill) => {
      items.push({ ...skill, category });
    });
  });
  return items;
}

export default function Step1BasicInfo() {
  const { form, categories, stepErrors: errors, updateForm: onChange, updateSeniority: onSeniorityChange } =
    useJobWizard();

  const [skillsGrouped, setSkillsGrouped] = useState({});
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [skillsError, setSkillsError] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    let cancelled = false;

    async function loadSkills() {
      setSkillsLoading(true);
      setSkillsError(false);
      try {
        const res = await apiGet('/api/v1/skills');
        if (!res.ok) throw new Error('skills');
        const json = await res.json();
        if (!cancelled) {
          setSkillsGrouped(json.data && typeof json.data === 'object' ? json.data : {});
        }
      } catch {
        if (!cancelled) {
          setSkillsError(true);
          setSkillsGrouped({});
        }
      } finally {
        if (!cancelled) setSkillsLoading(false);
      }
    }

    loadSkills();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedIds = useMemo(() => new Set(form.skill_ids || []), [form.skill_ids]);
  const atLimit = selectedIds.size >= MAX_SKILLS;
  const searchTerm = skillSearch.trim().toLowerCase();

  const allSkills = useMemo(() => flattenGroupedSkills(skillsGrouped), [skillsGrouped]);

  const selectedSkills = useMemo(
    () => allSkills.filter((skill) => selectedIds.has(skill.id)),
    [allSkills, selectedIds]
  );

  const filteredFlat = useMemo(() => {
    if (!searchTerm) return [];
    return allSkills.filter((skill) => skill.name.toLowerCase().includes(searchTerm));
  }, [allSkills, searchTerm]);

  const toggleSkill = (skillId) => {
    const current = form.skill_ids || [];
    if (selectedIds.has(skillId)) {
      onChange(
        'skill_ids',
        current.filter((id) => id !== skillId)
      );
      return;
    }
    if (current.length >= MAX_SKILLS) return;
    onChange('skill_ids', [...current, skillId]);
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const renderSkillCheckbox = (skill) => {
    const checked = selectedIds.has(skill.id);
    const disabled = !checked && atLimit;

    return (
      <Form.Check
        key={skill.id}
        type="checkbox"
        id={`rwm-wizard-1-skill-${skill.id}`}
        label={skill.name}
        checked={checked}
        disabled={disabled}
        onChange={() => toggleSkill(skill.id)}
        className={disabled ? 'text-muted' : ''}
        data-testid={`rwm-wizard-1-skill-${skill.id}`}
      />
    );
  };

  return (
    <div className="rwm-wizard-step">
      <h2 className="rwm-wizard-step__title">Basic Info</h2>
      <p className="rwm-wizard-step__intro text-muted">
        Start with the role title, category, employment type, and seniority levels.
      </p>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="rwm-wizard-1-title">Job title</Form.Label>
        <Form.Control
          id="rwm-wizard-1-title"
          type="text"
          maxLength={100}
          value={form.title}
          onChange={(e) => onChange('title', e.target.value)}
          isInvalid={Boolean(errors.title)}
          data-testid="rwm-wizard-1-title"
        />
        <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="rwm-wizard-1-category">Category</Form.Label>
        <Form.Select
          id="rwm-wizard-1-category"
          value={form.category_id}
          onChange={(e) => onChange('category_id', e.target.value)}
          isInvalid={Boolean(errors.category_id)}
          data-testid="rwm-wizard-1-category"
        >
          <option value="">Select a category</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors.category_id}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="rwm-wizard-1-employment">Employment type</Form.Label>
        <Form.Select
          id="rwm-wizard-1-employment"
          value={form.employment_type}
          onChange={(e) => onChange('employment_type', e.target.value)}
          isInvalid={Boolean(errors.employment_type)}
          data-testid="rwm-wizard-1-employment"
        >
          {EMPLOYMENT_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors.employment_type}</Form.Control.Feedback>
      </Form.Group>

      <fieldset className="mb-4">
        <legend className="form-label">Seniority level (select all that apply)</legend>
        {SENIORITY_OPTIONS.map((opt) => (
          <Form.Check
            key={opt.value}
            type="checkbox"
            id={`rwm-wizard-1-seniority-${opt.value}`}
            label={opt.label}
            checked={Boolean(form.senioritySelection[opt.value])}
            onChange={(e) => onSeniorityChange(opt.value, e.target.checked)}
            data-testid={`rwm-wizard-1-seniority-${opt.value}`}
          />
        ))}
        {errors.seniority_level ? (
          <div className="text-danger small mt-1" data-testid="rwm-wizard-1-seniority-error">
            {errors.seniority_level}
          </div>
        ) : null}
      </fieldset>

      <div className="mb-0">
        <h3 className="h6 mb-1">Skills (optional)</h3>
        <p className="text-muted small mb-2">Up to 10 skills — helps candidates filter by expertise</p>

        {selectedSkills.length > 0 ? (
          <div className="d-flex flex-wrap gap-2 mb-2">
            {selectedSkills.map((skill) => (
              <button
                key={skill.id}
                type="button"
                className="rwm-skill-badge border-0"
                onClick={() => toggleSkill(skill.id)}
                data-testid={`rwm-wizard-1-skill-badge-${skill.id}`}
              >
                {skill.name}
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        ) : null}

        <p className="small mb-2" data-testid="rwm-wizard-1-skills-count">
          Selected: {selectedIds.size}/{MAX_SKILLS}
        </p>

        <Form.Group className="mb-2">
          <Form.Control
            type="search"
            placeholder="Search skills..."
            value={skillSearch}
            onChange={(e) => setSkillSearch(e.target.value)}
            data-testid="rwm-wizard-1-skill-search"
          />
        </Form.Group>

        {skillsError ? (
          <p className="text-muted small mb-0" data-testid="rwm-wizard-1-skills-error">
            Skills unavailable
          </p>
        ) : skillsLoading ? (
          <p className="text-muted small mb-0">Loading skills…</p>
        ) : searchTerm ? (
          <div className="rwm-wizard-step__skill-list">
            {filteredFlat.length === 0 ? (
              <p className="text-muted small mb-0">No matching skills.</p>
            ) : (
              filteredFlat.map((skill) => renderSkillCheckbox(skill))
            )}
          </div>
        ) : (
          <div className="rwm-wizard-step__skill-list">
            {Object.entries(skillsGrouped).map(([category, skills]) => {
              if (!Array.isArray(skills) || skills.length === 0) return null;
              const expanded = Boolean(expandedCategories[category]);
              return (
                <div key={category} className="mb-2">
                  <button
                    type="button"
                    className="rwm-skill-category__header btn btn-link p-0 text-decoration-none text-body w-100 text-start"
                    onClick={() => toggleCategory(category)}
                    aria-expanded={expanded}
                  >
                    {expanded ? '▾' : '▸'} {category}
                  </button>
                  {expanded ? (
                    <div className="ps-2">
                      {skills.map((skill) => renderSkillCheckbox(skill))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
